import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";
import { MEDIA, STUDIO_INFO } from "@/data/content";
import BookCallDialog from "@/components/site/BookCallDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Music Photography",
  "Cinematic Video",
  "Film Production",
  "Full Rollout",
  "Other",
];

const BUDGETS = ["Under $5k", "$5k – $15k", "$15k – $50k", "$50k+"];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  project_type: "Music Photography",
  budget: "$5k – $15k",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Valid email required";
    if (!form.project_type) next.project_type = "Pick a project type";
    if (!form.message.trim() || form.message.trim().length < 10)
      next.message = "Tell us a bit more (min 10 chars)";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_type: form.project_type,
        budget: form.budget || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Inquiry received. We'll be in touch within 48 hours.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Something broke. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <img src={MEDIA.textureBg} alt="" className="w-full h-full object-cover" aria-hidden />
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-6">
            // 06 — Book the Studio
          </p>
          <h2
            data-testid="contact-title"
            className="font-display uppercase text-white text-5xl sm:text-6xl md:text-7xl leading-[0.9]"
          >
            Let's make <br /> a statement.
          </h2>
          <p className="mt-8 text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg">
            Tell us about the record, the rollout, or the vision. Every inquiry
            gets a personal response within 48 hours — no forms to a black hole.
          </p>

          <div className="mt-12 space-y-5">
            <div className="flex items-start gap-4 border-t border-white/10 pt-5">
              <EnvelopeSimple size={22} weight="light" className="text-[#E50914] mt-1" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Inquiries
                </div>
                <a
                  href={`mailto:${STUDIO_INFO.emailPrimary}`}
                  data-testid="contact-email-link"
                  className="text-white hover:text-[#E50914] transition-colors block"
                >
                  {STUDIO_INFO.emailPrimary}
                </a>
                <a
                  href={`mailto:${STUDIO_INFO.emailStudio}`}
                  data-testid="contact-email-secondary"
                  className="text-zinc-400 hover:text-white transition-colors block text-sm mt-1"
                >
                  {STUDIO_INFO.emailStudio}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 border-t border-white/10 pt-5">
              <Phone size={22} weight="light" className="text-[#E50914] mt-1" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Studio Line
                </div>
                <a
                  href={`tel:${STUDIO_INFO.phoneTel}`}
                  data-testid="contact-phone-link"
                  className="text-white hover:text-[#E50914] transition-colors"
                >
                  {STUDIO_INFO.phoneDisplay}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 border-t border-white/10 pt-5">
              <MapPin size={22} weight="light" className="text-[#E50914] mt-1" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Studio
                </div>
                <span className="text-white block" data-testid="contact-address">
                  {STUDIO_INFO.address.line1}
                </span>
                <span className="text-white block">
                  {STUDIO_INFO.address.line2}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-white/10 pt-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
              Prefer to talk live?
            </div>
            <BookCallDialog />
          </div>
        </div>

        <div className="lg:col-span-7 relative">
          <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-10">
            {done ? (
              <div
                data-testid="contact-success"
                className="flex flex-col items-start gap-5 py-10"
              >
                <CheckCircle size={48} weight="duotone" className="text-[#E50914]" />
                <h3 className="font-display uppercase text-white text-3xl md:text-4xl leading-none">
                  Inquiry received.
                </h3>
                <p className="text-zinc-300 max-w-md">
                  Thanks for reaching out. We'll review the brief and respond
                  within 48 hours — usually sooner.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="mt-4 border border-white/25 hover:bg-white hover:text-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    error={errors.name}
                    testid="contact-input-name"
                    placeholder="Jane Doe"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    error={errors.email}
                    testid="contact-input-email"
                    placeholder="you@label.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    label="Phone (optional)"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    testid="contact-input-phone"
                    placeholder="+1 (___) ___-____"
                  />
                  <SelectField
                    label="Budget"
                    name="budget"
                    value={form.budget}
                    onChange={onChange}
                    options={BUDGETS}
                    testid="contact-input-budget"
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-3">
                    Project Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_TYPES.map((pt) => {
                      const active = form.project_type === pt;
                      return (
                        <button
                          type="button"
                          key={pt}
                          onClick={() =>
                            setForm((f) => ({ ...f, project_type: pt }))
                          }
                          data-testid={`contact-project-${pt.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`font-mono text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors duration-200 ${
                            active
                              ? "bg-[#E50914] border-[#E50914] text-white"
                              : "bg-transparent border-white/15 text-zinc-300 hover:border-white/50"
                          }`}
                        >
                          {pt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-3"
                  >
                    Tell us about the project
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    placeholder="The artist, the record, the vibe, the timeline…"
                    className="w-full bg-black border border-white/15 focus:border-[#E50914] focus:outline-none px-4 py-3 text-white text-sm placeholder:text-zinc-600 transition-colors resize-none"
                  />
                  {errors.message && (
                    <p className="mt-2 text-[#E50914] font-mono text-[10px] uppercase tracking-[0.2em]">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center justify-center gap-3 bg-[#E50914] hover:bg-[#B9090B] disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
                  >
                    {submitting ? "Sending…" : "Send Inquiry"}
                    <PaperPlaneTilt size={16} weight="bold" />
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    We reply within 48 hours.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, error, testid, placeholder }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-3"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black border border-white/15 focus:border-[#E50914] focus:outline-none px-4 py-3 text-white text-sm placeholder:text-zinc-600 transition-colors"
      />
      {error && (
        <p className="mt-2 text-[#E50914] font-mono text-[10px] uppercase tracking-[0.2em]">
          {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-3"
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-black border border-white/15 focus:border-[#E50914] focus:outline-none px-4 py-3 text-white text-sm transition-colors appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23E50914' d='M6 8 0 0h12z'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 1rem center",
          paddingRight: "2.5rem",
        }}
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-black text-white">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
